import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AssetQuickLaunchModal } from './AssetQuickLaunchModal';
import type { AssetQuickLaunchPrefill, AssetQuickLaunchTarget } from './types';
import { isQuickLaunchAssetType } from './utils';
import { useCreateFromTemplate } from 'components/Minimenus/hooks/useCreateFromTemplate';

export type UseAssetQuickLaunchResult = {
  openQuickLaunch: (target: AssetQuickLaunchTarget) => void;
  closeQuickLaunch: () => void;
  modal: ReactNode;
  isQuickLaunchAssetType: (assetType: string | null | undefined) => boolean;
};

export type UseAssetQuickLaunchOptions = {
  onClose?: () => void;
};

export const useAssetQuickLaunch = (
  options: UseAssetQuickLaunchOptions = {},
): UseAssetQuickLaunchResult => {
  const { onClose } = options;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<AssetQuickLaunchTarget | null>(null);

  // Hook for creating assets from story templates
  const { createFromTemplate } = useCreateFromTemplate({
    openInNewTab: false,
    onSuccess: () => {
      setIsOpen(false);
      setTarget(null);
      onClose?.();
    },
    onError: () => {
      // Keep modal open on error so user can retry
    },
  });

  const closeQuickLaunch = useCallback(() => {
    setIsOpen(false);
    setTarget(null);
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(
    (prefill: AssetQuickLaunchPrefill) => {
      if (!target) {
        setIsOpen(false);
        return;
      }

      const sanitizedEntries = Object.entries(prefill ?? {}).filter(([, value]) => {
        return value !== undefined;
      });
      const sanitizedPayload = Object.fromEntries(
        sanitizedEntries,
      ) as AssetQuickLaunchPrefill;

      // If a story template was selected, use the template creation flow
      if (sanitizedPayload.storyTemplateId) {
        const { storyTemplateId, ...quickLaunchValues } = sanitizedPayload;
        createFromTemplate({
          templateId: storyTemplateId,
          quickLaunchPrefill: quickLaunchValues,
        });
        // Don't close modal here - it will be closed on success
        return;
      }

      if (typeof target.onSubmit === 'function') {
        target.onSubmit(sanitizedPayload);
      } else if (target.navigatePath) {
        navigate(target.navigatePath, {
          state: {
            assetQuickLaunch: sanitizedPayload,
          },
        });
      } else {
        console.warn('[useAssetQuickLaunch] Missing navigatePath or onSubmit handler.');
      }

      setIsOpen(false);
      setTarget(null);
    },
    [createFromTemplate, navigate, target],
  );

  const openQuickLaunch = useCallback((nextTarget: AssetQuickLaunchTarget) => {
    if (!nextTarget.navigatePath && typeof nextTarget.onSubmit !== 'function') {
      console.warn('[useAssetQuickLaunch] Target missing navigation and submit handler.');
      return;
    }

    setTarget(nextTarget);
    setIsOpen(true);
  }, []);

  const modal = useMemo(() => {
    if (!target) {
      return null;
    }

    return (
      <AssetQuickLaunchModal
        open={isOpen}
        assetLabel={target.assetLabel}
        assetType={target.assetType}
        actionLabel={target.actionLabel}
        currentTitle={target.currentTitle}
        defaultWorkflowId={target.defaultWorkflowId}
        defaultStartTime={target.defaultStartTime}
        defaultSlug={target.defaultSlug}
        mode={target.mode}
        onClose={closeQuickLaunch}
        onSubmit={handleSubmit}
        prefill={target.prefill}
      />
    );
  }, [closeQuickLaunch, handleSubmit, isOpen, target]);

  return {
    openQuickLaunch,
    closeQuickLaunch,
    modal,
    isQuickLaunchAssetType,
  };
};
